import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiArrowLeft, FiEdit2, FiPrinter, FiLock } from "react-icons/fi";
import salaryService from "../../services/salaryService";
import { useApp } from "../../context/AppContext";
import CollegePrintHeader from "../../components/common/CollegePrintHeader";
import { numberToWordsRupees } from "../../utils/numberToWords";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;
const MONTHS = ["", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

function Row({ label, value, bold = false, color }) {
    return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border, #e2e8f0)" }}>
            <span style={{ fontSize: 13, color: "var(--text-muted, #64748b)" }}>{label}</span>
            <span style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: color || "var(--text, #0f172a)" }}>{value}</span>
        </div>
    );
}

export default function PayslipView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useApp();

    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        salaryService.getById(id)
            .then((res) => setRecord(res.data))
            .catch((err) => { toast.error(err.message); navigate("/payroll"); })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    if (loading) return <div className="loading-center"><div className="spinner" />Loading payslip...</div>;
    if (!record) return null;

    const emp = record.employee || {};
    const isFinalized = record.status === "Finalized";
    const college = emp.college || user?.linkedCollege || {};
    const netPayInWords = numberToWordsRupees(record.netPay);

    return (
        <div>
            {/* ── Header (no-print) ── */}
            <div className="page-header no-print" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">Payslip — {emp.fullName}</h1>
                    <p className="page-subtitle">{MONTHS[record.month]} {record.year}</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/payroll")}>
                        <FiArrowLeft size={14} /> Back
                    </button>
                    {!isFinalized && (
                        <button className="btn btn-outline" onClick={() => navigate(`/payroll/${id}/edit`)}>
                            <FiEdit2 size={14} /> Edit
                        </button>
                    )}
                    <button className="btn btn-success" onClick={() => window.print()}>
                        <FiPrinter size={14} /> Print Payslip
                    </button>
                </div>
            </div>

            {isFinalized && (
                <div className="no-print" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "0.65rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#15803d", display: "flex", alignItems: "center", gap: 6 }}>
                    <FiLock size={13} /> This payslip is finalized and cannot be edited.
                </div>
            )}

            {/* ── Payslip Card (printable) ── */}
            <div className="card print-area payslip-document" style={{ maxWidth: 750, margin: "0 auto", padding: "1.5rem" }}>
                <style>{`
                    @media print {
                        @page {
                            size: A4 portrait;
                            margin: 0.35in;
                        }
                        body {
                            background: #fff !important;
                        }
                        .payslip-document {
                            border: 1.5px solid #0f172a !important;
                            box-shadow: none !important;
                            width: 100% !important;
                            max-width: 100% !important;
                            padding: 1.25rem !important;
                        }
                    }
                `}</style>

                {/* ── Dynamic College Header ── */}
                <CollegePrintHeader
                    college={college}
                    layout="banner"
                    logoSize={62}
                    title={`SALARY SLIP FOR THE MONTH OF ${MONTHS[record.month]?.toUpperCase()} ${record.year}`}
                    subtitle={isFinalized ? "Status: FINALIZED" : "Status: DRAFT"}
                    rightContent={
                        <div style={{ textAlign: "right", fontSize: "11px", color: "#475569" }}>
                            <div>Slip Ref: <strong>PS-{record.year}-{String(record.month).padStart(2, "0")}-{emp.employeeCode || id.slice(-4)}</strong></div>
                            <div>Issue Date: {new Date().toLocaleDateString("en-IN")}</div>
                        </div>
                    }
                />

                {/* Employee Details */}
                <div style={{ padding: "0.75rem 0", borderBottom: "1px solid var(--border, #e2e8f0)", marginBottom: "1rem" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem 2rem" }}>
                        {[
                            ["Employee Name", emp.fullName],
                            ["Employee Code", emp.employeeCode],
                            ["Department", emp.department],
                            ["Designation", emp.designation],
                            ["Category", emp.employeeCategory],
                            ["Contact Number", emp.mobile || "—"],
                        ].map(([label, value]) => (
                            <div key={label}>
                                <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted, #64748b)", fontWeight: 700 }}>{label}</span>
                                <div style={{ fontSize: 13.5, fontWeight: 600, marginTop: 1, color: "#0f172a" }}>{value}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Salary details: two columns */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 2rem", borderBottom: "1px solid var(--border, #e2e8f0)", paddingBottom: "1rem" }}>
                    {/* Earnings */}
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted, #64748b)", marginBottom: "0.6rem" }}>Earnings</div>
                        <Row label="Basic Pay" value={inr(record.basicPay)} />
                        <Row label="Dearness Allowance (DA)" value={inr(record.da)} />
                        <Row label="House Rent Allowance (HRA)" value={inr(record.hra)} />
                        <Row label="Other Allowances" value={inr(record.otherAllowances)} />
                        <Row label="Gross Pay" value={inr(record.grossPay)} bold color="#d97706" />
                    </div>

                    {/* Deductions */}
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted, #64748b)", marginBottom: "0.6rem" }}>Deductions</div>
                        <Row label="LOP Deduction" value={inr(record.lopDeduction)} color={record.lopDeduction > 0 ? "#dc2626" : undefined} />
                        <Row label="Provident Fund (PF)" value={inr(record.pf)} />
                        <Row label="Professional Tax (PT)" value={inr(record.pt)} />
                        <Row label="Tax Deducted at Source (TDS)" value={inr(record.tds)} />
                        <Row label="Other Deductions" value={inr(record.otherDeductions)} />
                        <Row label="Total Deductions" value={inr(record.totalDeductions)} bold color="#dc2626" />
                    </div>
                </div>

                {/* Attendance Summary */}
                <div style={{ padding: "0.75rem 0", borderBottom: "1px solid var(--border, #e2e8f0)", display: "flex", gap: "2.5rem", flexWrap: "wrap", background: "#f8fafc", margin: "1rem 0", borderRadius: 6 }}>
                    {[
                        ["Working Days", record.workingDays],
                        ["Present Days", record.presentDays],
                        ["LOP Days", record.lopDays],
                        ["Paid Leaves", record.paidLeaveDays],
                    ].map(([label, val]) => (
                        <div key={label} style={{ textAlign: "center", flex: 1 }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{val}</div>
                            <div style={{ fontSize: 10.5, color: "var(--text-muted, #64748b)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}>{label}</div>
                        </div>
                    ))}
                </div>

                {/* Net Pay Callout */}
                <div style={{
                    padding: "0.85rem 1.25rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#f0fdf4",
                    border: "1.5px solid #86efac",
                    borderRadius: 6,
                    marginBottom: "1rem"
                }}>
                    <div>
                        <div style={{ fontSize: 11, color: "#166534", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 800 }}>Net Salary Payable</div>
                        <div style={{ fontSize: 26, fontWeight: 900, color: "#15803d" }}>{inr(record.netPay)}</div>
                        <div style={{ fontSize: 11.5, color: "#166534", marginTop: 2, fontWeight: 600 }}>{netPayInWords}</div>
                    </div>
                    {record.remarks && (
                        <div style={{ maxWidth: 260, fontSize: 11.5, color: "#475569", fontStyle: "italic", textAlign: "right" }}>
                            Note: {record.remarks}
                        </div>
                    )}
                </div>

                {/* Signature line (print only) */}
                <div className="only-print" style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #0f172a", marginTop: "3rem", paddingTop: "0.5rem" }}>
                    <div style={{ textAlign: "center", width: "200px" }}>
                        <div style={{ paddingTop: 4, fontSize: 11, fontWeight: 700 }}>Employee Signature</div>
                    </div>
                    <div style={{ textAlign: "center", width: "200px" }}>
                        <div style={{ paddingTop: 4, fontSize: 11, fontWeight: 700 }}>Authorized Signatory / Principal</div>
                    </div>
                </div>
            </div>
        </div>
    );
}